import React, { useState, useMemo } from "react";
import { apiService } from "./services/apiService";
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaDownload, 
  FaEnvelope, 
  FaFileExcel, 
  FaSearch, 
  FaSort, 
  FaSortUp, 
  FaSortDown 
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./styles/Reports.css";

function AdmissionsReport({ onBack }) {
  // Helper functions to get default dates formatted as YYYY-MM-DD
  const getFirstDayOfCurrentMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Preselected date state values
  const [fromDate, setFromDate] = useState(getFirstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(getTodayDate());
  
  const [statusFilter, setStatusFilter] = useState({ active: true, inactive: false });
  const [studentsList, setStudentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // State to manage sorting configuration
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Helper to dynamically compute active status based on the report's dates
  const checkIsActive = (startDate, endDate) => {
    const today = new Date().toISOString().split("T")[0];
    return startDate <= today && endDate >= today;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);

    const params = {
      fromDate,
      toDate,
      active: statusFilter.active,
      inactive: statusFilter.inactive,
    };

    apiService.getAdmissionsReport(params)
      .then((data) => {
        setStudentsList(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  // Sorting Handler Logic
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Memoized sorted array processing computation
  const sortedStudentsList = useMemo(() => {
    let sortableItems = [...studentsList];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Custom computed value mapping fallback for the dynamic Status field
        if (sortConfig.key === "status") {
          aValue = checkIsActive(a.startDate, a.endDate) ? "Active" : "Inactive";
          bValue = checkIsActive(b.startDate, b.endDate) ? "Active" : "Inactive";
        }

        // Keep null/undefined records consistently at the bottom
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        // Specialized comparison execution for dates
        if (sortConfig.key === "startDate" || sortConfig.key === "endDate") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        // Specialized comparison execution for numeric values
        if (sortConfig.key === "feesPaid") {
          const numA = Number(aValue) || 0;
          const numB = Number(bValue) || 0;
          return sortConfig.direction === "asc" ? numA - numB : numB - numA;
        }

        // Default alphabetic string and regular localized value checking
        const stringA = String(aValue).toLowerCase();
        const stringB = String(bValue).toLowerCase();

        if (stringA < stringB) return sortConfig.direction === "asc" ? -1 : 1;
        if (stringA > stringB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [studentsList, sortConfig]);

  // Memoized total fees calculation based on the visible sorted/filtered items
  const totalFees = useMemo(() => {
    return sortedStudentsList.reduce((sum, s) => sum + (Number(s.feesPaid) || 0), 0);
  }, [sortedStudentsList]);

  // Renders state-dependent sort directional styling arrows inline inside headers
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "6px", opacity: 0.4 }} />;
    return sortConfig.direction === "asc" 
      ? <FaSortUp style={{ marginLeft: "6px", color: "#007bff" }} /> 
      : <FaSortDown style={{ marginLeft: "6px", color: "#007bff" }} />;
  };

  const buildPDFDocInstance = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    
    doc.text("Admissions Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Period: ${fromDate || "N/A"} to ${toDate || "N/A"}`, 14, 21);
    doc.setFontSize(9);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 26);

    autoTable(doc, {
      startY: 32,
      head: [["#", "Reg No.", "Name", "Mobile", "Seat", "Start Date", "End Date", "Fees", "Status"]],
      body: sortedStudentsList.map((s, index) => [
        index + 1,
        s.registrationNumber, 
        s.studentName, 
        s.mobileNumber, 
        s.seatCode || "N/A", 
        s.startDate || "N/A",
        s.endDate || "N/A",
        s.feesPaid != null ? `${s.feesPaid}` : "—",
        checkIsActive(s.startDate, s.endDate) ? "Active" : "Inactive"
      ]),
      foot: [["", "", "", "", "", "", "Total Fees:", `${totalFees}`, ""]]
    });
    return doc;
  };

  const handleDownloadXls = () => {
    const wb = XLSX.utils.book_new();
    const formattedData = sortedStudentsList.map((s, index) => ({
      "#": index + 1,
      "Registration No": s.registrationNumber,
      "Student Name": s.studentName,
      "Mobile Number": s.mobileNumber,
      "Seat Code": s.seatCode || "N/A",
      "Start Date": s.startDate || "N/A",
      "End Date": s.endDate || "N/A",
      "Fees Paid": s.feesPaid || 0,
      "Status": checkIsActive(s.startDate, s.endDate) ? "Active" : "Inactive"
    }));

    // Append the Summary Total Row to the Excel dataset
    formattedData.push({
      "#": "",
      "Registration No": "",
      "Student Name": "",
      "Mobile Number": "",
      "Seat Code": "",
      "Start Date": "",
      "End Date": "Total Fees:",
      "Fees Paid": totalFees,
      "Status": ""
    });

    // Append Generated Timestamp row at the very bottom
    formattedData.push({
      "#": "Report Generated On:",
      "Registration No": new Date().toLocaleString()
    });

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(formattedData), "Admissions");
    XLSX.writeFile(wb, `Admissions-Report-${fromDate}-to-${toDate}.xlsx`);
  };

  const handleEmailBoth = async () => {
    setEmailing(true);
    try {
      const doc = await buildPDFDocInstance();
      const pdfBlob = new Blob([doc.output("arraybuffer")], { type: "application/pdf" });

      const wb = XLSX.utils.book_new();
      const formattedData = sortedStudentsList.map((s, index) => ({
        "#": index + 1,
        "Registration No": s.registrationNumber,
        "Student Name": s.studentName,
        "Mobile Number": s.mobileNumber,
        "Seat Code": s.seatCode || "N/A",
        "Start Date": s.startDate || "N/A",
        "End Date": s.endDate || "N/A",
        "Fees Paid": s.feesPaid || 0,
        "Status": checkIsActive(s.startDate, s.endDate) ? "Active" : "Inactive"
      }));

      // Append the Summary Total Row to the Emailed Excel data collection
      formattedData.push({
        "#": "",
        "Registration No": "",
        "Student Name": "",
        "Mobile Number": "",
        "Seat Code": "",
        "Start Date": "",
        "End Date": "Total Fees:",
        "Fees Paid": totalFees,
        "Status": ""
      });

      // Append Generated Timestamp row at the bottom of emailing excel sheet
      formattedData.push({
        "#": "Report Generated On:",
        "Registration No": new Date().toLocaleString()
      });

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(formattedData), "Admissions");
      const xlsBlob = new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });

      const fd = new FormData();
      fd.append("pdf", pdfBlob, "AdmissionsReport.pdf");
      fd.append("xlsx", xlsBlob, "AdmissionsReport.xlsx");
      
      await apiService.emailAdmissionsReport(fd);
      alert("Email sent successfully!");
    } catch (err) { 
      alert("Email failed."); 
    }
    setEmailing(false);
  };

  return (
    <div className="rp-page">
      {/* Upper Control Bar */}
      <div className="rp-header-card">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="rp-header-icon"><FaCalendarAlt size={20} /></div>
            <h2 className="rp-title" style={{ margin: 0 }}>Admissions Report</h2>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {studentsList.length > 0 && (
            <>
              <button onClick={() => buildPDFDocInstance().then(d => d.save("Admissions-Report.pdf"))} className="rp-btn-back"><FaDownload /> PDF</button>
              <button onClick={handleDownloadXls} className="rp-btn-back"><FaFileExcel /> Excel</button>
              <button onClick={handleEmailBoth} disabled={emailing} className="rp-btn-back"><FaEnvelope /> {emailing ? "Sending..." : "Email Both"}</button>
            </>
          )}
          <button onClick={onBack} className="rp-btn-back"><FaArrowLeft /> Back To Dashboard</button>
        </div>
      </div>

      {/* Dynamic Search Filters Card */}
      <div className="rp-header-card">
        <form onSubmit={handleSearch} style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontSize: "13px", fontWeight: "700", color: "#7a8ba6", userSelect: "none" }}>FROM:</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rp-input" required />
          </div>
          
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ fontSize: "13px", fontWeight: "700", color: "#7a8ba6", userSelect: "none" }}>TO:</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rp-input" required />
          </div>

          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "700", color: "#7a8ba6", cursor: "pointer", userSelect: "none" }}>
              <input 
                type="checkbox" 
                checked={statusFilter.active} 
                onChange={e => setStatusFilter({ ...statusFilter, active: e.target.checked })} 
                style={{ width: "16px", height: "16px" }}
              />
              ACTIVE
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "700", color: "#7a8ba6", cursor: "pointer", userSelect: "none" }}>
              <input 
                type="checkbox" 
                checked={statusFilter.inactive} 
                onChange={e => setStatusFilter({ ...statusFilter, inactive: e.target.checked })} 
                style={{ width: "16px", height: "16px" }}
              />
              INACTIVE
            </label>
          </div>

          <button type="submit" disabled={loading} className="rp-btn-back" style={{ padding: "10px 20px" }}>
            <FaSearch /> {loading ? "Searching..." : "Search"}
          </button>
        </form>
      </div>

      {/* Render Results Table */}
      {studentsList.length > 0 ? (
        <div className="rp-card">
          <div className="rp-table-wrapper">
            <table className="rp-table">
              <thead>
                <tr>
                  <th style={{ userSelect: "none", width: "60px" }}>#</th>
                  <th onClick={() => handleSort("registrationNumber")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Reg No. {renderSortIcon("registrationNumber")}
                  </th>
                  <th onClick={() => handleSort("studentName")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Name {renderSortIcon("studentName")}
                  </th>
                  <th onClick={() => handleSort("mobileNumber")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Mobile {renderSortIcon("mobileNumber")}
                  </th>
                  <th onClick={() => handleSort("seatCode")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Seat {renderSortIcon("seatCode")}
                  </th>
                  <th onClick={() => handleSort("startDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Start Date {renderSortIcon("startDate")}
                  </th>
                  <th onClick={() => handleSort("endDate")} style={{ cursor: "pointer", userSelect: "none" }}>
                    End Date {renderSortIcon("endDate")}
                  </th>
                  <th onClick={() => handleSort("feesPaid")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Last Paid {renderSortIcon("feesPaid")}
                  </th>
                  <th onClick={() => handleSort("status")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Status {renderSortIcon("status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStudentsList.map((s, index) => {
                  const isActive = checkIsActive(s.startDate, s.endDate);
                  return (
                    <tr key={s.id}>
                      <td style={{ fontWeight: "600", color: "#7a8ba6" }}>{index + 1}</td>
                      <td>{s.registrationNumber}</td>
                      <td className="rp-td-name">{s.studentName}</td>
                      <td>{s.mobileNumber}</td>
                      <td>{s.seatCode || "—"}</td>
                      <td>{s.startDate || "—"}</td>
                      <td>{s.endDate || "—"}</td>
                      <td className="rp-td-fees">₹{s.feesPaid}</td>
                      <td>
                        <span className={`rp-seat-badge ${isActive ? "active-badge" : "inactive-badge"}`} style={{ 
                          backgroundColor: isActive ? "#e8f5e9" : "#ffebee",
                          color: isActive ? "#2e7d32" : "#c62828",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                
                {/* Total Fees Row */}
                <tr style={{ fontWeight: "700", borderTop: "2px solid #dee2e6", backgroundColor: "rgba(0,0,0,0.01)" }}>
                  <td colSpan="7" style={{ textAlign: "right", color: "#7a8ba6", padding: "12px" }}>Total Fees:</td>
                  <td className="rp-td-fees">₹{totalFees}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        !loading && fromDate && (
          <div className="rp-card" style={{ textAlign: "center", padding: "30px", color: "#7a8ba6", fontWeight: "600" }}>
            No records found for the selected criteria.
          </div>
        )
      )}
    </div>
  );
}

export default AdmissionsReport;